import type { LocalDatabaseProduct } from "../../../Data/_Bases/BaseProductRepository";
import type { LocalDatabasePromotion } from "../../../Data/_Bases/BasePromotionRepository";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import { Knapsack } from "./Knapsack";
import { PromotionManager } from "./PromotionManager";
import { groupProductsByUUID, type SalePromotionProduct } from "./PromotionUtilities";
import { RebateCalculation } from "./RebateCalculation";

export class RebateCalculationManager {
    constructor(
        protected manager: PromotionManager,
        protected products: Array<SalePromotionProduct>,
        protected promotions: Array<LocalDatabasePromotion>
    ) {}

    public async run(): Promise<void> {
        const grouped = groupProductsByUUID(this.products, false);
        const dbProducts = await this.manager.productRepository.getBulk(Object.keys(grouped));
        const products = ObjectPool.Get<Record<string, LocalDatabaseProduct>>();

        for(const product of dbProducts) {
            products[product.uuid] = product;
        }

        ArrayPool.Release(dbProducts);

        const matrices = ObjectPool.Get<Record<string, Array<{
            promotion: string;
            rebates: Array<number>;
        }>>>();

        for(const promotion of this.promotions) {
            const calculator = new RebateCalculation(this.manager, promotion, grouped, products);
            const matrix = calculator.runMatrix();
            const isSpendAndGet = promotion.criteria.some(criteria => criteria.buy_amount_type === "amount");

            for(const product in matrix) {
                if(!Object.hasOwn(matrix, product)) continue;

                const id = isSpendAndGet ? `${product}-spend` : product;

                if(typeof matrices[id] === "undefined") {
                    matrices[id] = ArrayPool.Get<{
                        promotion: string;
                        rebates: Array<number>;
                    }>();
                }

                const rebateObj = ObjectPool.Get<{
                    promotion: string;
                    rebates: Array<number>;
                }>();

                rebateObj.promotion = promotion.uuid;
                rebateObj.rebates = matrix[product];

                matrices[id].push(rebateObj);
            }
        }

        const rebatesToApply = ObjectPool.Get<Record<string, Array<{
            promotion: string;
            rate: number;
            remainingQuantity: number;
            isSpendAndGet: boolean;
        }>>>();

        for(const matrixId in matrices) {
            if(!Object.hasOwn(matrices, matrixId)) continue;

            const knapsack = Knapsack.getObject();
            const knapsackMap = ObjectPool.Get<Record<string, number>>();
            let maxQuantity = 1;

            for(const rebateOption of matrices[matrixId]) {
                for(let i = 0, l = rebateOption.rebates.length; i < l; i++) {
                    const id = `${rebateOption.promotion}-${i}`;

                    knapsack.prices.push(rebateOption.rebates[i]);
                    knapsack.quantities.push(i + 1);
                    knapsack.maxQuantities.push(1);
                    knapsack.identifiers.push(id);

                    knapsackMap[id] = knapsack.identifiers.length - 1;
                }

                maxQuantity = Math.max(rebateOption.rebates.length, maxQuantity);
            }

            let productId = matrixId;
            let knapsackQuantity = maxQuantity;
            let isSpendAndGet = false;

            if(productId.endsWith("-spend")) {
                productId = productId.slice(0, -6);
                isSpendAndGet = true;
            } else {
                knapsackQuantity = grouped[productId].total_quantity;
            }

            const ks = Knapsack.solve(
                knapsack,
                knapsackQuantity,
                false
            );

            if(ks.solution) {
                const toApply = ArrayPool.Get<{
                    promotion: string;
                    rate: number;
                    remainingQuantity: number;
                    isSpendAndGet: boolean;
                }>();

                for(let i = 0, l = ks.used.length; i < l; i++) {
                    const id = ks.used[i];

                    const application = ObjectPool.Get<{
                        promotion: string;
                        rate: number;
                        remainingQuantity: number;
                        isSpendAndGet: boolean;
                    }>();

                    const quantity = knapsack.quantities[knapsackMap[id]];

                    application.promotion = id.split(/[-|:]/g)[0];
                    application.rate = knapsack.prices[knapsackMap[id]] / quantity;
                    application.remainingQuantity = quantity;
                    application.isSpendAndGet = isSpendAndGet;

                    toApply.push(application);
                }

                if(typeof rebatesToApply[productId] !== "undefined") {
                    // We need to compare this set of rebates against the other (as we're using spend & get and a
                    // "normal" promotion)
                    const currentRebate = toApply.reduce(
                        (previous, rebate) => rebate.rate * rebate.remainingQuantity + previous,
                        0
                    );

                    const alternateRebate = rebatesToApply[productId].reduce(
                        (previous, rebate) => rebate.rate * rebate.remainingQuantity + previous,
                        0
                    );

                    if(currentRebate <= alternateRebate) {
                        continue;
                    }
                }

                rebatesToApply[productId] = toApply;
            }

            ObjectPool.Release(ks);
        }

        // Apply the rebate to each product
        for(let i = 0, l = this.products.length; i < l; i++) {
            // Clear the old rebate
            this.products[i].rebates = ObjectPool.Get();

            // Check if we have rebates for this product
            if(typeof rebatesToApply[this.products[i].uuid] === "undefined") {
                continue;
            }

            // Get the product's quantity
            const quantity = this.products[i].quantity;

            // Apply the rebate
            for(const rebate of rebatesToApply[this.products[i].uuid]) {
                let quantityToUse = Math.min(rebate.remainingQuantity, quantity);

                if(rebate.isSpendAndGet) {
                    quantityToUse = rebate.remainingQuantity;
                }

                if(quantityToUse <= 0) {
                    continue;
                }

                if(typeof this.products[i].rebates[rebate.promotion] === "undefined") {
                    this.products[i].rebates[rebate.promotion] = ObjectPool.Get();
                    this.products[i].rebates[rebate.promotion].amount = 0;
                    this.products[i].rebates[rebate.promotion].quantity = 0;
                }

                this.products[i].rebates[rebate.promotion].amount += rebate.rate * quantityToUse;
                this.products[i].rebates[rebate.promotion].quantity += quantityToUse;

                rebate.remainingQuantity -= quantityToUse;
            }
        }

        ObjectPool.Release(grouped);
        ObjectPool.Release(rebatesToApply);
    }
}
